import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, FileSearch, Link2, Globe2, ArrowRight, CheckCircle2, Clock, Scale, GraduationCap, Flame, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ZERO_COMMISSION } from "@/components/Disclaimers";
import { getPricing } from "@/lib/api";

const fade = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };
const curSym = (c) => (c === "eur" ? "€" : c === "usd" ? "$" : "");

const PreviewCard = ({ country, type, score, verified }) => (
  <div className="rounded-xl border bg-white p-4 shadow-[0_1px_0_rgba(15,39,64,0.06),0_10px_30px_rgba(15,39,64,0.06)]">
    <div className="flex items-center justify-between">
      <div className="font-display font-semibold text-[hsl(var(--atlas-navy))]">{country}</div>
      <Badge className={verified ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}>
        {verified ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
        {verified ? "Verified" : "Pending"}
      </Badge>
    </div>
    <div className="text-xs text-muted-foreground mt-1">{type}</div>
    <div className="mt-3 h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
      <div className="h-full rounded-full bg-[hsl(var(--atlas-navy))]" style={{ width: `${score}%` }} />
    </div>
    <div className="mt-1 text-[11px] text-muted-foreground tabular-nums">Accessibility {score}/100</div>
  </div>
);

const PromoHeroBadge = ({ pricing }) => {
  if (!pricing || !pricing.promo?.active) return null;
  const c = curSym(pricing.currency);
  return (
    <div
      data-testid="landing-promo-badge"
      className="mt-6 inline-flex flex-wrap items-center gap-2 rounded-full border border-[hsl(var(--atlas-teal))]/40 bg-[hsl(var(--atlas-teal))]/10 px-3 py-1.5 text-xs"
    >
      <Flame className="h-3.5 w-3.5 text-[hsl(var(--atlas-teal))]" />
      <span className="font-medium text-[hsl(var(--atlas-navy))]">
        {pricing.promo.label || "Launch promo"}:
      </span>
      <span className="text-[hsl(var(--atlas-slate))] tabular-nums">
        {c}{pricing.citizenship.price}
        <span className="ml-1 line-through opacity-60" data-testid="landing-promo-citizenship-strike">
          {c}{pricing.citizenship.pre_promo_price}
        </span>
        {" Citizenship · "}
        {c}{pricing.full.price}
        <span className="ml-1 line-through opacity-60" data-testid="landing-promo-full-strike">
          {c}{pricing.full.pre_promo_price}
        </span>
        {" Full Mobility"}
      </span>
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  useEffect(() => { getPricing().then(setPricing).catch(() => {}); }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 atlas-hero-wash" style={{ height: "100%" }} />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial="hidden" animate="show" variants={fade} transition={{ duration: 0.5 }} className="lg:col-span-7">
            <Badge variant="secondary" className="mb-4 gap-1.5 text-[hsl(var(--atlas-navy))]">
              <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> Commission-free by design
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[hsl(var(--atlas-navy))] leading-[1.05]">
              Which citizenships do you <span className="text-[hsl(var(--atlas-teal))]">already qualify</span> for?
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[hsl(var(--atlas-slate))] max-w-xl">
              Most people never check. Enter your nationalities and ancestry, then get a personalized, citation-backed report of the second citizenships and residence permits you can realistically obtain.
            </p>
            <ul className="mt-6 space-y-2">
              {[
                "No commissions from any government, developer, fund, or law firm. Ever.",
                "Primary-source citations with last-verified dates",
                "Two tiers: Citizenship Report or Full Mobility Report. Pay once, no subscription",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-[hsl(var(--atlas-slate))]">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--success))] shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <PromoHeroBadge pricing={pricing} />
            {/* Two equal-weight primary entry points so the matcher (discovery
                mode) sits alongside eligibility (heritage mode) above the
                fold on every viewport. Tax Explorer is demoted to a text
                link — it's a deep-dive tool, not a top-of-funnel doorway. */}
            <div className="mt-6 text-xs uppercase tracking-wider text-[hsl(var(--atlas-steel))]" data-testid="landing-hero-cta-hint">
              Two ways in — pick whichever feels right
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="landing-hero-primary-cta"
                size="lg"
                onClick={() => navigate("/check")}
                className="bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white"
              >
                Check your eligibility <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                data-testid="landing-hero-match-cta"
                size="lg"
                onClick={() => navigate("/match")}
                className="bg-[hsl(var(--atlas-teal))] hover:bg-[hsl(var(--atlas-teal-2,var(--atlas-teal)))] text-white"
              >
                <Compass className="mr-2 h-4 w-4" /> Find your fit
              </Button>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              Or <button
                data-testid="landing-hero-tax-link"
                onClick={() => navigate("/tax")}
                className="underline underline-offset-2 hover:text-[hsl(var(--atlas-navy))]"
              >explore the global tax data</button> first.
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Globe2 className="h-3.5 w-3.5" /> ~200 countries</span>
              <span className="flex items-center gap-1"><Scale className="h-3.5 w-3.5" /> Descent · Marriage · Naturalization · Investment</span>
            </div>
            <div data-testid="landing-zero-commission-statement" className="mt-6 flex items-start gap-2 rounded-xl border border-[hsl(var(--success))]/25 bg-[hsl(var(--success))]/5 px-4 py-3 max-w-xl">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--success))]" />
              <p className="text-xs text-[hsl(var(--atlas-slate))]">{ZERO_COMMISSION}</p>
            </div>
          </motion.div>
          <motion.div initial="hidden" animate="show" variants={fade} transition={{ duration: 0.6, delay: 0.15 }} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl bg-white/40 blur-xl" />
              <div className="relative space-y-3">
                <div className="text-xs font-medium uppercase tracking-wider text-[hsl(var(--atlas-steel))] mb-1">Sample report preview</div>
                <PreviewCard country="Italy" type="Citizenship by descent (grandparent)" score={88} verified />
                <PreviewCard country="Spain" type="Fast-track naturalization (2 years)" score={58} verified />
                <PreviewCard country="Portugal" type="Residence → citizenship pathway" score={46} verified={false} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECOND DOOR — /match preference matcher.
          Discovery-mode entry point for visitors who don't yet know which
          countries to consider. Sits between the hero and How-It-Works. */}
      <section
        className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
        data-testid="landing-second-door"
      >
        <Card className="p-8 bg-gradient-to-br from-white to-[hsl(var(--atlas-teal))]/5 border-[hsl(var(--atlas-teal))]/30">
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
            <div>
              <Badge className="mb-3 gap-1.5 bg-[hsl(var(--atlas-teal))]/15 text-[hsl(var(--atlas-navy))] border border-[hsl(var(--atlas-teal))]/40">
                <Compass className="h-3.5 w-3.5" /> Not sure where to start?
              </Badge>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[hsl(var(--atlas-navy))]">
                Find the countries that fit the{" "}
                <em className="text-[hsl(var(--atlas-teal))] not-italic">life you want</em>.
              </h2>
              <p className="mt-3 text-muted-foreground max-w-lg">
                Rank climate, cost of living, healthcare, safety, coast and
                English usability. Two-minute quiz. We&apos;ll shortlist ten
                countries with cited data. Free.
              </p>
              <div className="mt-5 flex gap-3">
                <Link to="/match">
                  <Button
                    className="bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white h-11 px-5"
                    data-testid="landing-second-door-cta"
                  >
                    Take the 2-minute match <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Climate", value: "1-5 zones" },
                  { label: "Cost", value: "Numbeo + PPP" },
                  { label: "Safety", value: "Global Peace Index" },
                  { label: "Healthcare", value: "WHO tier" },
                  { label: "Coast", value: "Yes / No" },
                  { label: "English", value: "EF EPI" },
                ].map((it) => (
                  <div key={it.label} className="rounded-lg border bg-white/70 p-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {it.label}
                    </div>
                    <div className="mt-1 font-medium text-[hsl(var(--atlas-navy))]">
                      {it.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14" data-testid="landing-how-it-works">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[hsl(var(--atlas-navy))]">How it works</h2>
        <p className="text-muted-foreground mt-2 max-w-2xl">Three steps. No account needed to see your results summary.</p>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            { icon: FileSearch, title: "1. Tell us your story", body: "Your nationalities, birthplace, and your parents' & grandparents' origins. Takes about 3 minutes." },
            { icon: Globe2, title: "2. See your matches", body: "Our rule-based engine checks every country's descent, marriage, naturalization, investment & residence routes." },
            { icon: Link2, title: "3. Get the receipts", body: "Unlock a ranked report. Each pathway cited to a primary source with a last-verified date, plus a branded PDF." },
          ].map((s) => (
            <Card key={s.title} className="p-6 card-hover bg-white">
              <div className="h-11 w-11 rounded-xl bg-[hsl(var(--accent))] flex items-center justify-center">
                <s.icon className="h-5 w-5 text-[hsl(var(--atlas-navy))]" />
              </div>
              <div className="mt-4 font-display font-semibold text-lg text-[hsl(var(--atlas-navy))]">{s.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* FRESHNESS / SOURCES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14" data-testid="landing-freshness-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge variant="secondary" className="mb-3">The moat is freshness</Badge>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[hsl(var(--atlas-navy))]">Every claim, sourced and dated</h2>
            <p className="mt-3 text-muted-foreground">
              Programs change fast: Malta&apos;s investor-citizenship scheme was struck down in 2025, Japan and Sweden lengthened naturalization for 2026. Static PDFs and stale blogs can&apos;t keep up. We show the source and the last-verified date on every pathway, and flag anything older than 12 months.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 text-sm"><GraduationCap className="h-4 w-4 text-[hsl(var(--atlas-navy))]" /> Professor-built methodology</div>
              <div className="flex items-center gap-2 text-sm"><ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" /> Conflict-free guidance</div>
            </div>
          </div>
          <Card className="bg-white overflow-hidden">
            <div className="grid grid-cols-3 text-xs font-medium bg-[hsl(var(--muted))] px-4 py-2 text-[hsl(var(--atlas-slate))]">
              <div>Pathway</div><div>Last verified</div><div>Status</div>
            </div>
            {[
              ["Italy · descent", "2026-01", true],
              ["Malta · investment", "2025-07", true],
              ["Romania · restoration", "2024-02", false],
            ].map(([p, d, ok]) => (
              <div key={p} className="grid grid-cols-3 items-center px-4 py-3 border-t text-sm">
                <div className="text-[hsl(var(--atlas-navy))]">{p}</div>
                <div className="text-muted-foreground tabular-nums">{d}</div>
                <div>
                  <Badge className={ok ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}>
                    {ok ? "Verified" : "Verification pending"}
                  </Badge>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-6">
        <div className="rounded-2xl border bg-white p-8 sm:p-12 text-center relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 atlas-divider" />
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[hsl(var(--atlas-navy))]">You may already qualify through your grandparents.</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">The eligibility check is free. You only pay to unlock the full, sourced report.</p>
          <Button size="lg" onClick={() => navigate("/check")} data-testid="landing-bottom-cta"
            className="mt-6 bg-[hsl(var(--atlas-navy))] hover:bg-[hsl(var(--atlas-navy-2))] text-white">
            Start your free check <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="mt-4 text-xs text-muted-foreground">
            Already purchased? <Link to="/check" className="underline">Retrieve your report</Link> with your email on the results page.
          </div>
        </div>
      </section>
    </div>
  );
}
